import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Package, Clock, CheckCircle, MapPin, Shield, Users, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { mockData } from '../data/mockData';
import Footer from '../components/Footer';

const LandingPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const navigate = useNavigate();

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  React.useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="landing-page">
      {/* Parallax Background */}
      <div style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: `translate(-50%, calc(-50% + ${scrollY * 0.3}px))`,
        fontSize: '12rem',
        fontWeight: '900',
        color: 'rgba(143, 236, 120, 0.25)',
        zIndex: 0,
        pointerEvents: 'none',
        whiteSpace: 'nowrap',
        textAlign: 'center',
        userSelect: 'none',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '2rem'
      }}>
        <img 
          src="https://customer-assets.emergentagent.com/job_returnpal-pickup/artifacts/x8ux114u_Screenshot%202026-04-27%20165324.png" 
          alt="" 
          style={{ 
            width: '300px', 
            height: 'auto',
            opacity: 0.25
          }}
        />
        <div>ReturnPal</div>
      </div>
      {/* Navigation Header */}
      <nav className="nav-header" style={{ position: 'relative', zIndex: 10 }}>
        <div className="nav-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
          <img 
            src="https://customer-assets.emergentagent.com/job_returnpal-pickup/artifacts/x8ux114u_Screenshot%202026-04-27%20165324.png" 
            alt="ReturnPal" 
            className="logo-image"
            style={{ height: '55px', width: 'auto' }}
          />
        </div>
        
        <div className={`nav-links ${isMenuOpen ? 'nav-links-mobile' : ''}`}>
          <button onClick={() => scrollToSection('why-returnpal')} className="nav-link">Why ReturnPal</button>
          <button onClick={() => scrollToSection('how-it-works')} className="nav-link">How It Works</button>
          <button onClick={() => scrollToSection('pricing')} className="nav-link">Pricing</button>
          <button onClick={() => scrollToSection('faq')} className="nav-link">FAQ</button>
          <button onClick={() => navigate('/contact')} className="nav-link">Contact</button>
          {/* Mobile-only: include CTA buttons in the dropdown so they're reachable on phones */}
          {isMenuOpen && (
            <div className="nav-cta-mobile-inline">
              <Button
                onClick={() => { setIsMenuOpen(false); navigate('/login'); }}
                variant="outline"
                data-testid="mobile-login-btn"
                style={{ color: 'rgb(0, 55, 32)', borderColor: 'rgb(143, 236, 120)', width: '100%' }}
              >
                Customer Login
              </Button>
              <Button
                onClick={() => { setIsMenuOpen(false); navigate('/create-account'); }}
                variant="outline"
                data-testid="mobile-signup-btn"
                style={{ color: 'rgb(0, 55, 32)', borderColor: 'rgb(143, 236, 120)', width: '100%' }}
              >
                Sign Up
              </Button>
              <Button
                onClick={() => { setIsMenuOpen(false); navigate('/signup'); }}
                className="btn-primary"
                data-testid="mobile-book-btn"
                style={{ background: 'linear-gradient(to bottom right, #8FEC78, #81DD67)', color: 'rgb(0, 55, 32)', fontWeight: '600', border: 'none', width: '100%' }}
              >
                Book a Pickup
              </Button>
            </div>
          )}
        </div>

        <div className="nav-cta">
          <Button 
            onClick={() => navigate('/login')} 
            variant="outline"
            style={{ 
              marginRight: '0.5rem',
              color: 'rgb(0, 55, 32)',
              borderColor: 'rgb(143, 236, 120)'
            }}
          >
            Customer Login
          </Button>
          <Button
            onClick={() => navigate('/create-account')}
            variant="outline"
            data-testid="nav-signup-btn"
            style={{
              marginRight: '0.5rem',
              color: 'rgb(0, 55, 32)',
              borderColor: 'rgb(143, 236, 120)'
            }}
          >
            Sign Up
          </Button>
          <Button 
            onClick={() => navigate('/signup')} 
            className="btn-primary"
            style={{ 
              background: 'linear-gradient(to bottom right, #8FEC78, #81DD67)',
              color: 'rgb(0, 55, 32)',
              fontWeight: '600',
              border: 'none'
            }}
          >
            Book a Pickup
          </Button>
        </div>

        <button className="nav-toggle" onClick={() => setIsMenuOpen(!isMenuOpen)}>
          <div className="hamburger"></div>
          <div className="hamburger"></div>
          <div className="hamburger"></div>
        </button>
      </nav>

      {/* Hero Section */}
      <section className="hero-section" style={{ position: 'relative', zIndex: 1 }}>
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">
              Make returns easy. Return with ReturnPal!
            </h1>
            <p className="hero-subtitle" style={{ 
              fontSize: '1.5rem',
              fontWeight: '700',
              lineHeight: '1.6',
              marginBottom: '1rem',
              color: 'rgb(0, 55, 32)'
            }}>
              Online shopping is convenient, now RETURNS are too!
            </p>
            <p style={{ 
              fontSize: '1.15rem', 
              lineHeight: '1.7',
              marginBottom: '1.25rem',
              color: 'rgb(0, 55, 32)'
            }}>
              ReturnPal picks up your prepaid returns at your workplace — no driving to drop-off locations, no waiting in line. We handle the drop-off, so you can focus on your day.
            </p>
            <p style={{ 
              fontSize: '1.05rem', 
              marginBottom: '2rem',
              color: '#047857',
              fontWeight: '500'
            }}>
              Prepaid UPS, FedEx, and eligible Amazon-approved returns. Packaging assistance available upon request.
            </p>
            <div className="hero-cta">
              <Button onClick={() => navigate('/signup')} className="btn-primary btn-large">
                Book a Pickup
              </Button>
              <Button 
                onClick={() => scrollToSection('pricing')} 
                variant="outline" 
                size="lg"
                style={{ 
                  borderColor: '#10b981',
                  color: '#10b981',
                  backgroundColor: 'white'
                }}
              >
                See Pricing
              </Button>
            </div>
            <p style={{
              marginTop: '1.5rem',
              fontSize: '0.875rem',
              color: '#6b7280',
              fontStyle: 'italic',
              textAlign: 'center'
            }}>
              Workplace pickup. Professional handling. No lines, no extra stops, no workflow disruption &amp; no more missing return deadlines.
            </p>
          </div>
        </div>
      </section>

      {/* Why ReturnPal Section */}
      <section id="why-returnpal" className="section-standard" style={{ position: 'relative', zIndex: 1 }}>
        <div className="container">
          <h2 className="section-title" style={{ fontSize: '2.5rem', fontWeight: '700', textAlign: 'center' }}>A simpler way to get your returns handled</h2>
          <p className="section-subtitle" style={{ maxWidth: '900px', margin: '0 auto 3rem', lineHeight: '1.8', fontSize: '1.125rem', textAlign: 'center' }}>
            ReturnPal is a logistics convenience service for workplace professionals who already have prepaid return labels and want the drop-off handled for them. We collect your return packages and deliver them to authorized carrier locations, so you can stay focused on your work instead of adding another errand to your day.
          </p>

          <div className="features-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem', marginTop: '3rem' }}>
            <div className="feature-card">
              <div className="feature-icon" style={{ fontSize: '3rem', marginBottom: '1rem' }}>⏱️</div>
              <h3 className="feature-title">Save time</h3>
              <p className="feature-description">
                No more driving to drop-off locations, waiting in line, or trying to squeeze returns into your schedule.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon" style={{ fontSize: '3rem', marginBottom: '1rem' }}>📅</div>
              <h3 className="feature-title">Stay on deadline</h3>
              <p className="feature-description">
                We deliver collected packages to authorized carrier locations within 48 calendar hours of pickup.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon" style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
              <h3 className="feature-title">Know exactly what to expect</h3>
              <p className="feature-description">
                ReturnPal handles prepaid package pickup and carrier drop-off only, with clear rules and transparent service boundaries.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Who It's For Section */}
      <section className="section-standard" style={{ backgroundColor: '#f9fafb' }}>
        <div className="container">
          <h2 className="section-title" style={{ fontSize: '2.5rem', fontWeight: '700', textAlign: 'center' }}>Built for workplace professionals who value their time and hate return errands!</h2>
          <p className="section-subtitle" style={{ maxWidth: '900px', margin: '0 auto 1.5rem', lineHeight: '1.8', fontSize: '1.125rem', textAlign: 'center' }}>
            If you already have a prepaid label but keep putting off the actual drop-off, ReturnPal is for you. Whether you're managing work deadlines, live a busy lifestyle juggling multiple responsibilities, or just trying to avoid one more stop on the way home, ReturnPal does the returns for you to not miss any more return deadlines. Perfect for busy professionals, frequent online shoppers, and anyone tired of return logistics getting in the way of their day.
          </p>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="section-standard">
        <div className="container">
          <h2 className="section-title" style={{ fontSize: '2.5rem', fontWeight: '700', textAlign: 'center' }}>How ReturnPal works</h2>
          <p className="section-subtitle" style={{ marginBottom: '3rem', textAlign: 'center' }}>
            Booking a return pickup is simple. Here's what to expect from start to finish.
          </p>

          <div className="steps-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '2.5rem', maxWidth: '1200px', margin: '0 auto' }}>
            <div className="step-card" style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-start' }}>
              <div className="step-number" style={{ 
                minWidth: '60px',
                height: '60px',
                borderRadius: '50%',
                backgroundColor: '#10b981',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.5rem',
                fontWeight: 'bold'
              }}>1</div>
              <div>
                <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>Schedule your pickup</h3>
                <p style={{ color: '#6b7280', lineHeight: '1.6' }}>
                  Choose a pickup date that works for you. Pickups are available Monday through Friday.
                </p>
              </div>
            </div>

            <div className="step-card" style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-start' }}>
              <div className="step-number" style={{ 
                minWidth: '60px',
                height: '60px',
                borderRadius: '50%',
                backgroundColor: '#10b981',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.5rem',
                fontWeight: 'bold'
              }}>2</div>
              <div>
                <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>Upload your return labels</h3>
                <p style={{ color: '#6b7280', lineHeight: '1.6' }}>
                  Upload one prepaid return label for each package. If multiple items are packed under one valid label, that works too.
                </p>
              </div>
            </div>

            <div className="step-card" style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-start' }}>
              <div className="step-number" style={{ 
                minWidth: '60px',
                height: '60px',
                borderRadius: '50%',
                backgroundColor: '#10b981',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.5rem',
                fontWeight: 'bold'
              }}>3</div>
              <div>
                <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>Leave your packages at your selected pickup location</h3>
                <p style={{ color: '#6b7280', lineHeight: '1.6' }}>
                  Place your return packages at the location you selected during booking during your scheduled pickup window.
                </p>
              </div>
            </div>

            <div className="step-card" style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-start' }}>
              <div className="step-number" style={{ 
                minWidth: '60px',
                height: '60px',
                borderRadius: '50%',
                backgroundColor: '#10b981',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.5rem',
                fontWeight: 'bold'
              }}>4</div>
              <div>
                <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>We handle the drop-off</h3>
                <p style={{ color: '#6b7280', lineHeight: '1.6' }}>
                  ReturnPal picks up your packages and delivers them to an authorized carrier location within 48 calendar hours. Weekend drop-offs may be used to help meet that timing window.
                </p>
              </div>
            </div>
          </div>

          <p style={{ textAlign: 'center', marginTop: '2.5rem', color: '#6b7280', fontStyle: 'italic', fontSize: '0.9375rem' }}>
            Need help getting your return boxed up? Packaging assistance and materials are available upon request.
          </p>
        </div>
      </section>

      {/* Service Scope Section */}
      <section className="section-standard" style={{ backgroundColor: '#f9fafb' }}>
        <div className="container">
          <h2 className="section-title" style={{ fontSize: '2.5rem', fontWeight: '700', textAlign: 'center' }}>What ReturnPal does and doesn't do</h2>
          <p className="section-subtitle" style={{ marginBottom: '3rem', textAlign: 'center' }}>
            ReturnPal is designed for prepaid return packages that are ready for carrier drop-off.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '2rem', maxWidth: '1000px', margin: '0 auto' }}>
            <div style={{ 
              backgroundColor: 'white', 
              padding: '2rem', 
              borderRadius: '12px',
              border: '2px solid #10b981',
              boxShadow: '0 4px 6px rgba(0,0,0,0.05)'
            }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#10b981', marginBottom: '1rem' }}>
                ✓ What we do
              </h3>
              <p style={{ color: '#374151', lineHeight: '1.7' }}>
                We pick up prepaid return packages, label your packages, offer packaging assistance when requested, and deliver collected returns to authorized carrier locations.
              </p>
            </div>

            <div style={{ 
              backgroundColor: 'white', 
              padding: '2rem', 
              borderRadius: '12px',
              border: '2px solid #dc2626',
              boxShadow: '0 4px 6px rgba(0,0,0,0.05)'
            }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#dc2626', marginBottom: '1rem' }}>
                ✗ What we don't do
              </h3>
              <p style={{ color: '#374151', lineHeight: '1.7' }}>
                We do not create return labels, process retailer counter returns, or operate as a shipping carrier.
              </p>
            </div>
          </div>

          <p style={{ textAlign: 'center', marginTop: '2rem', fontSize: '1rem', color: '#6b7280', fontStyle: 'italic' }}>
            That means a simpler process, clearer expectations, and fewer surprises for everyone.
          </p>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="section-standard">
        <div className="container">
          <h2 className="section-title" style={{ fontSize: '2.5rem', fontWeight: '700', textAlign: 'center' }}>Simple, transparent pricing</h2>
          <p className="section-subtitle" style={{ maxWidth: '900px', margin: '0 auto 3rem', lineHeight: '1.8', textAlign: 'center' }}>
            Choose a one-time pickup or a monthly plan based on how often you need returns handled. Every option is designed to keep the process convenient, predictable, and easy to use.
          </p>
          
          <div className="pricing-simple" style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(4, 1fr)', 
            gap: '1.5rem', 
            maxWidth: '1400px', 
            margin: '0 auto' 
          }}>
            
            {/* Let's Be Pals - Pay As You Go */}
            <Card className="pricing-card-main" style={{ border: '2px solid #10b981', padding: '1rem' }}>
              <CardHeader style={{ padding: '0.75rem 0' }}>
                <CardTitle className="pricing-title-main" style={{ fontSize: '1.25rem', marginBottom: '0.5rem' }}>Let's Be Pal</CardTitle>
                <CardDescription className="pricing-description" style={{ fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                  Pay As You Go
                </CardDescription>
                <div className="pricing-price-main" style={{ marginBottom: '0.25rem' }}>
                  <span className="price-currency" style={{ fontSize: '1rem' }}>$</span>
                  <span className="price-amount" style={{ fontSize: '2rem' }}>19.99</span>
                </div>
                <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.5rem' }}>
                  Best for occasional returns.
                </p>
              </CardHeader>
              <CardContent style={{ padding: '0.75rem 0' }}>
                <div className="pricing-additional" style={{ marginBottom: '1rem' }}>
                  <p className="pricing-addon" style={{ fontSize: '0.875rem', margin: '0.25rem 0' }}><strong>✓ 1 pickup</strong></p>
                  <p className="pricing-addon" style={{ fontSize: '0.875rem', margin: '0.25rem 0' }}><strong>✓ Up to 3 packages</strong></p>
                  <p className="pricing-note-small" style={{ marginTop: '0.5rem', color: '#6b7280', fontSize: '0.75rem' }}>
                    Perfect for first-time users
                  </p>
                </div>
                <Button 
                  onClick={() => navigate('/signup?plan=lets-be-pals')} 
                  className="btn-primary w-full"
                  style={{ padding: '0.625rem 1rem', fontSize: '0.875rem' }}
                >
                  Book One Pickup
                </Button>
              </CardContent>
            </Card>

            {/* Your Pal - Starter */}
            <Card className="pricing-card-main" style={{ border: '2px solid #3b82f6', padding: '1rem' }}>
              <CardHeader style={{ padding: '0.75rem 0' }}>
                <CardTitle className="pricing-title-main" style={{ fontSize: '1.25rem', marginBottom: '0.5rem' }}>Your Pal</CardTitle>
                <CardDescription className="pricing-description" style={{ fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                  Starter
                </CardDescription>
                <div className="pricing-price-main" style={{ marginBottom: '0.25rem' }}>
                  <span className="price-currency" style={{ fontSize: '1rem' }}>$</span>
                  <span className="price-amount" style={{ fontSize: '2rem' }}>39.99</span>
                  <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>/mo</span>
                </div>
                <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.5rem' }}>
                  A simple monthly option for lighter return volume.
                </p>
              </CardHeader>
              <CardContent style={{ padding: '0.75rem 0' }}>
                <div className="pricing-additional" style={{ marginBottom: '1rem' }}>
                  <p className="pricing-addon" style={{ fontSize: '0.875rem', margin: '0.25rem 0' }}><strong>✓ 2 pickups per month</strong></p>
                  <p className="pricing-addon" style={{ fontSize: '0.875rem', margin: '0.25rem 0' }}><strong>✓ Up to 5 packages per pickup</strong></p>
                  <p className="pricing-addon" style={{ fontSize: '0.875rem', margin: '0.25rem 0' }}><strong>✓ Up to 10 packages total per month</strong></p>
                </div>
                <Button 
                  onClick={() => navigate('/signup?plan=your-pal')} 
                  className="btn-primary w-full"
                  style={{ padding: '0.625rem 1rem', fontSize: '0.875rem' }}
                >
                  Subscribe Your Pal
                </Button>
              </CardContent>
            </Card>

            {/* My Pal - Standard */}
            <Card className="pricing-card-main" style={{ border: '3px solid #f59e0b', position: 'relative', padding: '1rem' }}>
              <div style={{ 
                position: 'absolute', 
                top: '-10px', 
                left: '50%', 
                transform: 'translateX(-50%)',
                backgroundColor: '#f59e0b',
                color: 'white',
                padding: '3px 12px',
                borderRadius: '12px',
                fontSize: '0.75rem',
                fontWeight: '600'
              }}>
                MOST POPULAR
              </div>
              <CardHeader style={{ padding: '0.75rem 0' }}>
                <CardTitle className="pricing-title-main" style={{ fontSize: '1.25rem', marginBottom: '0.5rem' }}>My Pal</CardTitle>
                <CardDescription className="pricing-description" style={{ fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                  Standard
                </CardDescription>
                <div className="pricing-price-main" style={{ marginBottom: '0.25rem' }}>
                  <span className="price-currency" style={{ fontSize: '1rem' }}>$</span>
                  <span className="price-amount" style={{ fontSize: '2rem' }}>49.99</span>
                  <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>/mo</span>
                </div>
                <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.5rem' }}>
                  Best for regular returners who want more flexibility.
                </p>
              </CardHeader>
              <CardContent style={{ padding: '0.75rem 0' }}>
                <div className="pricing-additional" style={{ marginBottom: '1rem' }}>
                  <p className="pricing-addon" style={{ fontSize: '0.875rem', margin: '0.25rem 0' }}><strong>✓ 3 pickups per month</strong></p>
                  <p className="pricing-addon" style={{ fontSize: '0.875rem', margin: '0.25rem 0' }}><strong>✓ Up to 5 packages per pickup</strong></p>
                  <p className="pricing-addon" style={{ fontSize: '0.875rem', margin: '0.25rem 0' }}><strong>✓ Up to 15 packages total per month</strong></p>
                </div>
                <Button 
                  onClick={() => navigate('/signup?plan=my-pal')} 
                  className="btn-primary w-full"
                  style={{ padding: '0.625rem 1rem', fontSize: '0.875rem', backgroundColor: '#f59e0b', borderColor: '#f59e0b' }}
                >
                  Subscribe My Pal
                </Button>
              </CardContent>
            </Card>

            {/* Best Pals - Pro */}
            <Card className="pricing-card-main" style={{ border: '2px solid #8b5cf6', padding: '1rem' }}>
              <CardHeader style={{ padding: '0.75rem 0' }}>
                <CardTitle className="pricing-title-main" style={{ fontSize: '1.25rem', marginBottom: '0.5rem' }}>Best Pals</CardTitle>
                <CardDescription className="pricing-description" style={{ fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                  Pro
                </CardDescription>
                <div className="pricing-price-main" style={{ marginBottom: '0.25rem' }}>
                  <span className="price-currency" style={{ fontSize: '1rem' }}>$</span>
                  <span className="price-amount" style={{ fontSize: '2rem' }}>59.99</span>
                  <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>/mo</span>
                </div>
                <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.5rem' }}>
                  For households or professionals with frequent return needs.
                </p>
              </CardHeader>
              <CardContent style={{ padding: '0.75rem 0' }}>
                <div className="pricing-additional" style={{ marginBottom: '1rem' }}>
                  <p className="pricing-addon" style={{ fontSize: '0.875rem', margin: '0.25rem 0' }}><strong>✓ 4 pickups per month</strong></p>
                  <p className="pricing-addon" style={{ fontSize: '0.875rem', margin: '0.25rem 0' }}><strong>✓ Up to 5 packages per pickup</strong></p>
                  <p className="pricing-addon" style={{ fontSize: '0.875rem', margin: '0.25rem 0' }}><strong>✓ Up to 20 packages total per month</strong></p>
                </div>
                <Button 
                  onClick={() => navigate('/signup?plan=best-pals')} 
                  className="btn-primary w-full"
                  style={{ padding: '0.625rem 1rem', fontSize: '0.875rem' }}
                >
                  Subscribe Best Pals
                </Button>
              </CardContent>
            </Card>

          </div>

          {/* Pickup cap side note */}
          <div
            data-testid="pickup-cap-note"
            style={{
              maxWidth: '1200px',
              margin: '2rem auto 0',
              padding: '1rem 1.25rem',
              backgroundColor: '#f0fdf4',
              border: '1px solid #bbf7d0',
              borderLeft: '4px solid #10b981',
              borderRadius: '8px',
              fontSize: '0.9rem',
              color: '#065f46',
              lineHeight: 1.6,
              textAlign: 'center',
            }}
          >
            <strong>Note:</strong> Each pickup accommodates a maximum of 10 packages. Unused packages roll over within the current month and reset at the beginning of the following month.
          </div>

          {/* Add-On Services */}
          <div style={{ marginTop: '2rem', padding: '1.5rem', backgroundColor: '#f9fafb', borderRadius: '12px', maxWidth: '1200px', margin: '2rem auto 0' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '0.5rem', textAlign: 'center', color: '#1f2937' }}>
              Add-on services and extra fees
            </h3>
            <p style={{ textAlign: 'center', fontSize: '0.9rem', color: '#6b7280', marginBottom: '1.5rem' }}>
              Need more flexibility? Add-on services are available when your return needs go beyond your plan.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '0.75rem' }}>
              <div style={{ textAlign: 'center', padding: '0.75rem' }}>
                <p style={{ fontSize: '0.8rem', color: '#1f2937', fontWeight: '600', marginBottom: '0.25rem' }}>Additional packages</p>
                <p style={{ fontSize: '0.95rem', fontWeight: '700', color: '#10b981' }}>$5 each</p>
                <p style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: '0.25rem' }}>Maximum 10 additional packages per pickup.</p>
              </div>
              <div style={{ textAlign: 'center', padding: '0.75rem' }}>
                <p style={{ fontSize: '0.8rem', color: '#1f2937', fontWeight: '600', marginBottom: '0.25rem' }}>Additional pickups</p>
                <p style={{ fontSize: '0.95rem', fontWeight: '700', color: '#10b981' }}>$15 each</p>
                <p style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: '0.25rem' }}>Available for subscription customers.</p>
              </div>
              <div style={{ textAlign: 'center', padding: '0.75rem' }}>
                <p style={{ fontSize: '0.8rem', color: '#1f2937', fontWeight: '600', marginBottom: '0.25rem' }}>Packaging materials</p>
                <p style={{ fontSize: '0.85rem', fontWeight: '600', color: '#10b981', lineHeight: '1.3' }}>
                  Small — $3<br />Medium — $4<br />Large — $5
                </p>
              </div>
              <div style={{ textAlign: 'center', padding: '0.75rem' }}>
                <p style={{ fontSize: '0.8rem', color: '#1f2937', fontWeight: '600', marginBottom: '0.25rem' }}>Rush drop-off</p>
                <p style={{ fontSize: '0.95rem', fontWeight: '700', color: '#10b981' }}>$10</p>
              </div>
              <div style={{ textAlign: 'center', padding: '0.75rem' }}>
                <p style={{ fontSize: '0.8rem', color: '#1f2937', fontWeight: '600', marginBottom: '0.25rem' }}>Oversized package</p>
                <p style={{ fontSize: '0.95rem', fontWeight: '700', color: '#10b981' }}>$8 per package</p>
              </div>
            </div>
            <p style={{ textAlign: 'center', marginTop: '0.75rem', fontSize: '0.8rem', color: '#6b7280', fontStyle: 'italic' }}>
              💡 Monthly package allowances roll over within the month and reset monthly.
            </p>
          </div>
        </div>
      </section>

      {/* Timing Section */}
      <section className="section-standard" style={{ backgroundColor: '#f9fafb' }}>
        <div className="container">
          <h2 className="section-title" style={{ fontSize: '2.5rem', fontWeight: '700', textAlign: 'center' }}>Pickup and drop-off timing</h2>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '2rem', maxWidth: '900px', margin: '2rem auto 0' }}>
            <div style={{ 
              backgroundColor: 'white', 
              padding: '2rem', 
              borderRadius: '12px',
              border: '2px solid #10b981',
              boxShadow: '0 4px 6px rgba(0,0,0,0.05)'
            }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#10b981', marginBottom: '1rem' }}>
                Pickup availability
              </h3>
              <p style={{ color: '#374151', lineHeight: '1.7', fontSize: '1rem' }}>
                Monday through Friday.
              </p>
            </div>

            <div style={{ 
              backgroundColor: 'white', 
              padding: '2rem', 
              borderRadius: '12px',
              border: '2px solid #10b981',
              boxShadow: '0 4px 6px rgba(0,0,0,0.05)'
            }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#10b981', marginBottom: '1rem' }}>
                Drop-off timing
              </h3>
              <p style={{ color: '#374151', lineHeight: '1.7', fontSize: '1rem' }}>
                Packages are delivered to authorized carrier locations within 48 calendar hours of collection. Weekend drop-offs may occur to help meet that 48-hour window.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Important Policy Section */}
      <section className="section-standard">
        <div className="container">
          <h2 className="section-title" style={{ fontSize: '2.5rem', fontWeight: '700', textAlign: 'center' }}>Important Return Deadline Policy</h2>
          
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <p style={{ 
              color: '#374151', 
              lineHeight: '1.8', 
              fontSize: '1.125rem',
              marginBottom: '1.5rem',
              textAlign: 'center'
            }}>
              To protect your return eligibility, pickups must be scheduled at least 48 hours before the retailer's final return deadline. Customers are responsible for confirming return eligibility, refund status, and retailer-specific requirements directly with the retailer. ReturnPal is an independent convenience service that simplifies prepaid return pickup and carrier drop-off and is not affiliated with any retailer, carrier, or marketplace.
            </p>
            
            <p style={{ 
              textAlign: 'center',
              fontSize: '1rem',
              color: '#6b7280',
              fontStyle: 'italic',
              marginTop: '1rem'
            }}>
              Carrier hours, holidays, and location availability may affect scheduling, so we recommend booking as early as possible. ReturnPal does not determine whether a retailer will accept a return or issue a refund.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="section-faq">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-2" style={{ fontSize: '2.5rem', fontWeight: '700', textAlign: 'center', marginBottom: '1rem' }}>Frequently Asked Questions</h2>
            <p style={{ textAlign: 'center', fontSize: '1.125rem', color: '#6b7280', maxWidth: '900px', margin: '0 auto 3rem', lineHeight: '1.7' }}>
              Have questions before booking? Here's what to know about how ReturnPal works, what we accept, and what to expect from pickup to drop-off.
            </p>
          </div>
          <div className="faq-container">
            <Accordion type="single" collapsible className="faq-accordion">
              <AccordionItem value="item-0">
                <AccordionTrigger className="faq-question">
                  How does ReturnPal work?
                </AccordionTrigger>
                <AccordionContent className="faq-answer">
                  ReturnPal picks up your prepaid return packages and delivers them to an authorized carrier drop-off location for you. Simply schedule a pickup, upload one prepaid return label for each package, and leave your packages at your selected pickup location during your pickup window. We handle the drop-off within 48 calendar hours of collection.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-1">
                <AccordionTrigger className="faq-question">
                  Does ReturnPal guarantee that my return will be accepted or refunded?
                </AccordionTrigger>
                <AccordionContent className="faq-answer">
                  No. ReturnPal does not determine whether a retailer will accept a return or issue a refund. Customers are responsible for confirming return eligibility, refund status, deadlines, and retailer-specific requirements directly with the retailer. ReturnPal is an independent convenience service that facilitates prepaid return pickup and carrier drop-off only.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-2">
                <AccordionTrigger className="faq-question">
                  Is ReturnPal affiliated with retailers or carriers?
                </AccordionTrigger>
                <AccordionContent className="faq-answer">
                  No. ReturnPal is a separate, independent service and is not a retailer, shipping carrier, freight forwarder, or refund processor. ReturnPal is not affiliated with, endorsed by, or acting on behalf of any retailer, marketplace, or carrier unless expressly stated otherwise in writing.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-3">
                <AccordionTrigger className="faq-question">
                  Which carriers do you support?
                </AccordionTrigger>
                <AccordionContent className="faq-answer">
                  We currently support prepaid return packages for UPS, FedEx, and eligible Amazon-approved returns. ReturnPal does not create return labels or process in-store retailer counter returns, so your package must already be prepared for carrier drop-off.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-4">
                <AccordionTrigger className="faq-question">
                  What if my return needs packaging?
                </AccordionTrigger>
                <AccordionContent className="faq-answer">
                  Packaging materials and packaging assistance can be added for an additional fee if needed during booking. Each package must have a valid prepaid return label.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-5">
                <AccordionTrigger className="faq-question">
                  Does ReturnPal cover residential areas?
                </AccordionTrigger>
                <AccordionContent className="faq-answer">
                  At this time, ReturnPal is focusing on professional office buildings, however if customers want to bring returned items from home they can use their office/business location for scheduling pick up.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-6">
                <AccordionTrigger className="faq-question">
                  What areas do you service?
                </AccordionTrigger>
                <AccordionContent className="faq-answer">
                  ReturnPal currently operates within approved commercial office clusters. Service availability is limited to designated buildings.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-7">
                <AccordionTrigger className="faq-question">
                  What are your service hours?
                </AccordionTrigger>
                <AccordionContent className="faq-answer">
                  Pickups are available <strong>Monday through Friday</strong> in two windows: <strong>11 AM – 1 PM</strong> (midday) or <strong>4 PM – 6 PM</strong> (end of day). After pickup, packages are delivered to authorized carrier drop-off locations within 48 calendar hours. Weekend drop-offs may be used when needed to help meet that 48-hour window.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-8">
                <AccordionTrigger className="faq-question">
                  What is your package limit?
                </AccordionTrigger>
                <AccordionContent className="faq-answer">
                  Maximum 10 packages per pickup. Package size limits: Standard packages up to 50 lbs and 150 inches (length + width + height combined). Oversized packages available with an $8 fee per package. All packages must have a valid prepaid return label or a return QR code.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-9">
                <AccordionTrigger className="faq-question">
                  What if my return deadline is close?
                </AccordionTrigger>
                <AccordionContent className="faq-answer">
                  To protect your return eligibility, pickups must be scheduled at least 48 hours before the retailer's final return deadline. Customers are responsible for confirming all retailer deadlines and return requirements directly with the retailer before booking.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

// Helper functions for icons
const getStepIcon = (iconName) => {
  const icons = {
    package: <Package size={32} />,
    clock: <Clock size={32} />,
    check: <CheckCircle size={32} />
  };
  return icons[iconName] || <Package size={32} />;
};

const getFeatureIcon = (iconName) => {
  const icons = {
    clock: <Clock size={24} />,
    mappin: <MapPin size={24} />,
    shield: <Shield size={24} />,
    users: <Users size={24} />,
    package: <Package size={24} />,
    check: <CheckCircle size={24} />
  };
  return icons[iconName] || <Package size={24} />;
};

export default LandingPage;
;
